import {
  useAppService,
  useGetNominatedPurchasersForOrgQuery,
  useSelectedOrgId,
} from "@fero/hooks";
import type { PurchaseRequestView } from "adl-gen/ferovinum/app/api";
import { Loader } from "components/widgets/loader/loader";
import { useCallback, useEffect, useState } from "react";
import { formatDateToISO8601 } from "utils/date-utils";
import { useLoadingDataState } from "utils/hooks/use-loading-data";
import { assertNotUndefined } from "utils/hx/util/types";
import { isLoaded } from "utils/utility-types";
import { PurchaseRequestsPageView } from "./organisation-purchase-requests-page-view";

export type PurchaseRequestsDashboardSection = "inProgress" | "completed";

export const OrganisationPurchaseRequestsPage = () => {
  const service = useAppService();
  const organisationId = assertNotUndefined(useSelectedOrgId());
  const [selectedSection, setSelectedSection] =
    useState<PurchaseRequestsDashboardSection>("inProgress");
  const [completedPurchaseRequestsLoaded, setCompletedPurchaseRequestsLoaded] =
    useState(false);
  const [completedPurchaseRequests, setCompletedPurchaseRequests] = useState<
    PurchaseRequestView[]
  >([]);

  // Load the nominated purchasers proactively in the background so when someone swaps to customers view it is already loaded
  useGetNominatedPurchasersForOrgQuery();

  const loadInProgressPurchaseRequests = useCallback(async () => {
    return await service.getPurchaseRequestsForOrganisation({
      organisationId,
      stateType: "in_progress",
      lookbackDays: null,
    });
  }, [organisationId, service]);

  const loadCompletedPurchaseRequests = useCallback(async () => {
    const completedPurchaseRequests =
      await service.getPurchaseRequestsForOrganisation({
        organisationId,
        stateType: "completed",
        lookbackDays: null,
      });
    setCompletedPurchaseRequests(completedPurchaseRequests);
    setCompletedPurchaseRequestsLoaded(true);
  }, [organisationId, service]);

  const onGenerateCSV = async (from: Date, to: Date) => {
    if (isLoaded(loadingData)) {
      await service.generatePurchaseRequestReportForOrgAndSendEmail({
        organisationId,
        stateType:
          selectedSection === "inProgress" ? "in_progress" : "completed",
        fromDate: formatDateToISO8601(from),
        toDate: formatDateToISO8601(to),
      });
    }
  };

  const [loadingData] = useLoadingDataState(loadInProgressPurchaseRequests);

  useEffect(() => {
    if (selectedSection === "completed" && !completedPurchaseRequestsLoaded) {
      loadCompletedPurchaseRequests();
    }
  }, [
    selectedSection,
    completedPurchaseRequestsLoaded,
    loadCompletedPurchaseRequests,
  ]);

  return (
    <Loader loadingStates={[loadingData]}>
      {isLoaded(loadingData) && (
        <PurchaseRequestsPageView
          inProgressPurchaseRequests={loadingData.value}
          completedPurchaseRequests={completedPurchaseRequests}
          onSelectSection={setSelectedSection}
          completedPurchaseRequestsLoaded={completedPurchaseRequestsLoaded}
          onGenerateCSV={onGenerateCSV}
        />
      )}
    </Loader>
  );
};
